// ═══════════════════════════════════════════════════════════════
// AdminFeedback — everything the beta has told you, in one list.
//
// Feedback that lives in a database console is feedback you read
// once. This is the triage surface: filter by state, see who said
// it and on what build, and move it along.
// ═══════════════════════════════════════════════════════════════
import React, { useMemo, useState } from 'react';
import { MessageSquare, Lightbulb, Bug, HelpCircle, Filter } from 'lucide-react';
import {
  useAllFeedback, setFeedbackStatus, FEEDBACK_STATUSES,
  feedbackKindLabel,
} from './lib/use-feedback.js';
import { toDate } from './lib/job-helpers.js';

function kindIcon(kind) {
  if (kind === 'feature')   return <Lightbulb size={13} />;
  if (kind === 'bug')       return <Bug size={13} />;
  if (kind === 'confusing') return <HelpCircle size={13} />;
  return <MessageSquare size={13} />;
}

function when(ts) {
  const d = toDate(ts);
  if (!d) return '';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export default function AdminFeedback() {
  const { items, ready, error } = useAllFeedback();
  const [filter, setFilter] = useState('open');
  const [busyId, setBusyId] = useState(null);

  const counts = useMemo(() => {
    const c = { all: items.length, open: 0 };
    FEEDBACK_STATUSES.forEach((s) => { c[s.value] = 0; });
    items.forEach((i) => {
      const st = i.status || 'new';
      c[st] = (c[st] || 0) + 1;
      if (st === 'new' || st === 'planned') c.open += 1;
    });
    return c;
  }, [items]);

  const shown = useMemo(() => {
    if (filter === 'all') return items;
    if (filter === 'open') {
      return items.filter((i) => (i.status || 'new') === 'new' || i.status === 'planned');
    }
    return items.filter((i) => (i.status || 'new') === filter);
  }, [items, filter]);

  async function move(id, status) {
    setBusyId(id);
    try {
      await setFeedbackStatus(id, status);
    } catch (e) {
      alert(`Could not update: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  }

  const tabs = [
    { key: 'open', label: `Open (${counts.open})` },
    ...FEEDBACK_STATUSES.map((s) => ({ key: s.value, label: `${s.label} (${counts[s.value] || 0})` })),
    { key: 'all', label: `All (${counts.all})` },
  ];

  return (
    <div className="fb-admin">
      <div className="screen-header">
        <div>
          <h1>Feedback</h1>
          <p className="subtitle">
            {ready ? `${items.length} submitted` : 'Loading…'}
          </p>
        </div>
      </div>

      {error && (
        <p className="form-error">
          Could not load feedback: {error.message}
        </p>
      )}

      <div className="fb-filter-row" role="tablist" aria-label="Filter feedback">
        <Filter size={13} className="fb-filter-icon" />
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={filter === t.key}
            className={`fb-filter-chip${filter === t.key ? ' active' : ''}`}
            onClick={() => setFilter(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {ready && shown.length === 0 && (
        <p className="muted small empty-inline">Nothing here.</p>
      )}

      <ul className="fb-list">
        {shown.map((f) => {
          const status = f.status || 'new';
          return (
            <li key={f.id} className={`fb-item fb-${status}`}>
              <div className="fb-item-head">
                <span className={`fb-kind-tag fb-kind-${f.kind || 'other'}`}>
                  {kindIcon(f.kind)} {feedbackKindLabel(f.kind)}
                </span>
                <span className="muted small fb-when">{when(f.createdAt)}</span>
              </div>

              <p className="fb-title">{f.title}</p>
              {f.detail && <p className="fb-detail">{f.detail}</p>}

              <p className="fb-who muted small">
                {f.userEmail || f.userId}
                {f.appVersion && <> · <span className="mono">{f.appVersion}</span></>}
                {f.env === 'sandbox' && <> · sandbox</>}
                {f.path && <> · {f.path}</>}
              </p>

              <div className="fb-actions">
                {FEEDBACK_STATUSES.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    disabled={busyId === f.id || status === s.value}
                    className={`fb-status-btn${status === s.value ? ' active' : ''}`}
                    onClick={() => move(f.id, s.value)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
